import React from 'react';
import { Typography, Checkbox } from '@navi/web-ui/lib/primitives';
import { Controller, Control } from 'react-hook-form';
import { AvailableColumns, Column, ColumnFormData } from '../interface';

interface ConfigDrawerColumnListProps {
  columnConfigurationLoading: boolean;
  columnConfigurationData: { availableColumns?: AvailableColumns } | null;
  control: Control<ColumnFormData>;
  isEditable: boolean;
  searchValue: string;
  filterColumns: (columns: Column[], category: string) => Column[];
  highlightText: (text: string, searchTerm: string) => React.ReactNode;
}

const ConfigDrawerColumnList: React.FC<ConfigDrawerColumnListProps> = ({
  columnConfigurationLoading,
  columnConfigurationData,
  control,
  isEditable,
  searchValue,
  filterColumns,
  highlightText
}) => {
  if (columnConfigurationLoading) {
    return (
      <div className="flex justify-center py-4">
        <Typography variant="p3" color="var(--navi-color-gray-c2)">
          Loading columns...
        </Typography>
      </div>
    );
  }

  if (!columnConfigurationData?.availableColumns) {
    return (
      <div className="flex justify-center py-4">
        <Typography variant="p3" color="var(--navi-color-gray-c2)">
          No columns available
        </Typography>
      </div>
    );
  }

  return (
    <div className="mt-4">
      {Object.entries(columnConfigurationData.availableColumns as AvailableColumns).map(
        ([category, columns]) => {
          const filteredColumns = filterColumns(columns, category);

          if (filteredColumns.length === 0) return null;

          return (
            <div key={category} className="mb-6">
              <Typography variant="h4" className="mb-3" color="var(--navi-color-gray-c1)">
                {highlightText(category, searchValue)}
              </Typography>
              <div className="space-y-2">
                {filteredColumns.map((column: Column) => (
                  <Controller
                    key={column.columnName}
                    name={`columns.${column.columnName}`}
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={field.value || false}
                          onChange={e => {
                            if (isEditable) {
                              field.onChange(e.target.checked);
                            }
                          }}
                          disabled={!isEditable}
                        />
                        <Typography variant="p3" color={'var(--navi-color-gray-c2)'}>
                          {highlightText(column.displayName, searchValue)}
                        </Typography>
                      </div>
                    )}
                  />
                ))}
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

export default ConfigDrawerColumnList;
